/**
 * Minimal markdown renderer for documents: headings, lists, tables, bold,
 * italics, inline code and links. Documents are authored only by snip.work
 * staff, but everything is still escaped first and links are forced to
 * https?: - a renderer should not be the reason a portal trusts its inputs.
 *
 * Deliberately not a dependency: the document format is ours, the subset is
 * small, and a full markdown library would be the largest thing in the bundle.
 */

function escapeHtml(text: string): string {
  return text
    .replaceAll('&', '&amp;').replaceAll('<', '&lt;').replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;').replaceAll("'", '&#39;');
}

function inline(text: string): string {
  return escapeHtml(text)
    .replace(/`([^`]+)`/g, '<code>$1</code>')
    .replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
    .replace(/\*([^*]+)\*/g, '<em>$1</em>')
    .replace(/\[([^\]]+)\]\((https?:[^)\s]+)\)/g,
      '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>');
}

export function renderMarkdown(source: string): string {
  const lines = source.replaceAll('\r\n', '\n').split('\n');
  const out: string[] = [];
  let list: 'ul' | 'ol' | null = null;
  let paragraph: string[] = [];
  let table: string[][] | null = null;

  const closeList = () => { if (list) { out.push(`</${list}>`); list = null; } };
  const closeParagraph = () => {
    if (paragraph.length) { out.push(`<p>${paragraph.map(inline).join(' ')}</p>`); paragraph = []; }
  };
  const closeTable = () => {
    if (!table) return;
    const [head, ...rows] = table;
    out.push('<table><thead><tr>');
    for (const cell of head) out.push(`<th>${inline(cell)}</th>`);
    out.push('</tr></thead><tbody>');
    for (const row of rows) {
      out.push('<tr>');
      for (const cell of row) out.push(`<td>${inline(cell)}</td>`);
      out.push('</tr>');
    }
    out.push('</tbody></table>');
    table = null;
  };
  const closeBlocks = () => { closeParagraph(); closeList(); closeTable(); };

  for (const raw of lines) {
    const line = raw.trimEnd();

    const heading = /^(#{1,3})\s+(.*)$/.exec(line);
    if (heading) {
      closeBlocks();
      out.push(`<h${heading[1].length}>${inline(heading[2])}</h${heading[1].length}>`);
      continue;
    }

    if (/^\|.*\|$/.test(line)) {
      closeParagraph(); closeList();
      const cells = line.slice(1, -1).split('|').map((c) => c.trim());
      // The |---|---| separator row carries no content.
      if (cells.every((c) => /^:?-{2,}:?$/.test(c))) continue;
      if (!table) table = [];
      table.push(cells);
      continue;
    }
    closeTable();

    const bullet = /^[-*]\s+(.*)$/.exec(line);
    const numbered = /^\d+\.\s+(.*)$/.exec(line);
    if (bullet || numbered) {
      closeParagraph();
      const kind = bullet ? 'ul' : 'ol';
      if (list !== kind) { closeList(); out.push(`<${kind}>`); list = kind; }
      out.push(`<li>${inline((bullet ?? numbered)![1])}</li>`);
      continue;
    }
    closeList();

    if (!line.trim()) { closeParagraph(); continue; }
    paragraph.push(line.trim());
  }

  closeBlocks();
  return out.join('');
}

export function Markdown({ source }: { source: string }) {
  // eslint-disable-next-line react/no-danger
  return <div className="prose-doc" dangerouslySetInnerHTML={{ __html: renderMarkdown(source) }} />;
}
